import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Lock, Plus, Star, Trash2, TriangleAlert, Layers, Radio, LogOut } from "lucide-react";
import SectionHead from "./DevSectionHead";
/* THE SHARED DEV MODAL SHELL lives in DeveloperAccounts.css — .dev-modal-back,
   .dev-modal, .dev-modal-head/body/foot/actions, .dev-list, .dev-help. This
   component renders a modal and is used on /dev/containers, which imports only
   DeveloperDashboard.css, so without this import the confirmation dialog comes
   up completely unstyled: an unpositioned block of text over the page, with a
   Delete button in it. CSS is global once imported, so this is enough.
   Worth extracting into its own DevModal.css when something else needs it. */
import "./DeveloperAccounts.css";

/**
 * CONTAINER BOOKS — create one, rename it, move the default, disable one.
 *
 * ITS OWN REQUEST, not part of the inventory that draws the rest of this page.
 * The inventory is a read of stored history and is cached and reused; this list
 * changes when somebody changes it, and has to come back changed. Folding it in
 * would mean a flag toggle either re-counted every deal in the database or left
 * the page showing the state before the click.
 *
 * EVERY WRITE RE-RENDERS FROM THE SERVER'S OWN LIST. Each response carries the
 * full `containers` array, and that is what lands in state — not a local edit to the
 * row that was clicked. Moving the default changes TWO rows, and a UI that
 * patched only the clicked one would show two defaults until the next reload.
 *
 * DISABLED PAIRS ARE LISTED HERE, and only here. They are gone from every
 * selector, the combined totals and session opening — so this page is the one
 * place their history is visible and the only route back to re-enabling them.
 * That is why they are dimmed and labelled rather than filtered out.
 *
 * WHAT IT REFUSES IS THE SERVER'S BUSINESS. The buttons for an impossible change
 * are disabled with a `title` saying why, but the rules themselves live in
 * routes/devPairsRoutes.js — the UI is not a second copy of them that can drift.
 * Anything it lets through comes back as a message from the server, shown as-is.
 */
export default function ContainerBooks({ token }) {
  const [containers, setContainers] = useState(null);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState("");
  const [name, setName] = useState("");
  const [mode, setMode] = useState("live");
  const [startDate, setStartDate] = useState("");
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState("");
  /* The container queued for deletion, and what the reader has typed to confirm
     it. Held together so closing the dialog cannot leave a stale confirmation
     armed for the next one. */
  const [doomed, setDoomed] = useState(null);
  const [typed, setTyped] = useState("");

  const load = useCallback(async () => {
    setErr("");
    try {
      const res = await fetch("/api/dev/containers", { headers: { Authorization: `Bearer ${token}` } });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.message || `container list failed (${res.status})`);
      setContainers(body.containers || []);
    } catch (e) {
      setErr(e.message);
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  /* One path for every write, because they all return the same thing: the whole
     list, and a sentence about what changed. */
  /* The account queued for release, held with its container so the dialog can
     name both. Release is not undoable in one click - the window closes at a
     real instant and re-admitting needs a fresh period date - so it is
     confirmed, but with a sentence rather than a typed name: nothing is
     destroyed, and the cost of a mistaken release is a rotation, not data. */
  const [releasing, setReleasing] = useState(null);

  const send = useCallback(
    async (method, path, payload, key) => {
      setBusy(key);
      setErr("");
      setMsg("");
      try {
        const res = await fetch(`/api/dev/containers${path}`, {
          method,
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
          /* No body on a DELETE. Sending `JSON.stringify(undefined)` puts the
             literal string "undefined" on the wire, which the JSON parser then
             rejects — a 400 that looks like the route refusing the delete. */
          ...(payload === undefined ? {} : { body: JSON.stringify(payload) }),
        });
        const body = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(body.message || `that failed (${res.status})`);
        if (body.containers) setContainers(body.containers);
        if (body.message) setMsg(body.message);
        return true;
      } catch (e) {
        setErr(e.message);
        return false;
      } finally {
        setBusy("");
      }
    },
    [token]
  );

  const create = async (e) => {
    e.preventDefault();
    if (!name.trim() || !startDate) return;
    if (await send("POST", "", { name: name.trim(), mode, startDate }, "new")) {
      setName("");
      setMode("live");
      setStartDate("");
    }
  };

  /**
   * DELETE, which the dialog below has already confirmed.
   *
   * The name is sent back on the URL because the ROUTE requires it: a delete
   * that only the UI guards is a delete anyone can make with curl by accident.
   * The typed confirmation in the dialog and the `?confirm=` here are the same
   * check, deliberately, so neither side can be the only one holding it.
   */
  const destroy = async (p) => {
    const ok = await send(
      "DELETE",
      `/${p.id}?confirm=${encodeURIComponent(p.name)}`,
      undefined,
      `x${p.id}`
    );
    if (ok) {
      setDoomed(null);
      setTyped("");
    }
  };

  const doRelease = async () => {
    if (!releasing) return;
    const ok = await send(
      "POST",
      `/${releasing.containerId}/release`,
      { login: releasing.login },
      `r${releasing.containerId}${releasing.login}`
    );
    if (ok) setReleasing(null);
  };

  const rename = async (p) => {
    const next = draft.trim();
    if (!next || next === p.name) {
      setEditing(null);
      return;
    }
    if (await send("PUT", `/${p.id}`, { name: next }, `n${p.id}`)) setEditing(null);
  };

  return (
    <section className="dev-section">
      <SectionHead icon={Layers} title="Container books" total={containers ? containers.length : undefined}>
        Each container keeps its own book — its own pool, its own settled and unsettled, its own
        opening capital. A <strong>disabled</strong> container leaves every dropdown, the combined
        totals and session opening, and stays listed here with its history intact. Accounts and its period boundary are set on <Link to="/dev/accounts">Accounts</Link>.
      </SectionHead>

      {err && (
        <div className="dev-error" role="alert">
          <TriangleAlert size={16} aria-hidden="true" />
          <span>{err}</span>
        </div>
      )}
      {msg && !err && <p className="dev-note dev-note-ok">{msg}</p>}

      <form className="dev-newcontainer" onSubmit={create}>
        <input
          className="dev-input"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="New container name — e.g. Container 2"
          maxLength={60}
          aria-label="New container name"
        />
        {/* REQUIRED, and the label says what it decides rather than just naming
            the field. It is where this container's history begins — what
            MT5_START_DATE used to say for the whole deployment — and there is
            no fallback, so a container cannot be created without one. */}
        <input
          className="dev-input dev-input-date"
          type="date"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          aria-label="Start date — where this container's history begins"
          title="Where this container's history begins. Overall and the period statistics are measured from it."
        />
        <select
          className="dev-input dev-input-sel"
          value={mode}
          onChange={(e) => setMode(e.target.value)}
          aria-label="Mode"
        >
          <option value="live">live</option>
          <option value="test">test</option>
        </select>
        <button
          className="dev-btn"
          type="submit"
          disabled={!name.trim() || !startDate || busy === "new"}
          title={!startDate ? "A start date is required" : "Create this container"}
        >
          <Plus size={14} aria-hidden="true" /> Create container
        </button>
      </form>

      {!containers && <p className="dev-note">Reading the container books…</p>}

      {containers && containers.length === 0 && (
        <p className="dev-warn">
          <TriangleAlert size={15} aria-hidden="true" />
          <span>
            There are no containers. Nothing can report until one exists — create one above, or run{" "}
            <code>scripts/migrate-to-containers.js</code> to file the existing history under a first
            container.
          </span>
        </p>
      )}

      <div className="dev-containergrid">
        {(containers || []).map((p) => (
          <article className={`dev-card dev-ccard${p.enabled ? "" : " is-off"}`} key={p.id}>
            <div className="dev-card-head">
              {editing === p.id ? (
                <input
                  className="dev-input dev-input-inline"
                  value={draft}
                  autoFocus
                  maxLength={60}
                  onChange={(e) => setDraft(e.target.value)}
                  onBlur={() => rename(p)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") rename(p);
                    if (e.key === "Escape") setEditing(null);
                  }}
                  aria-label={`Rename ${p.name}`}
                />
              ) : (
                <h3 className="dev-card-title">
                  <button
                    type="button"
                    className="dev-rename"
                    title="Rename this container"
                    onClick={() => {
                      setEditing(p.id);
                      setDraft(p.name);
                    }}
                  >
                    {p.name}
                  </button>
                </h3>
              )}
              <span className={`dev-tag${p.mode === "test" ? " dev-tag-test" : ""}`}>{p.mode}</span>
            </div>

            <div className="dev-ccard-marks">
              {p.isDefault && (
                <span className="dev-pill dev-pill-default" title="Pre-selected in every dropdown, and what /investment-portfolio reads">
                  <Star size={11} aria-hidden="true" /> default
                </span>
              )}
              {!p.enabled && <span className="dev-pill dev-pill-off">disabled</span>}
              {!p.includeInOverall && <span className="dev-pill">not in totals</span>}
              {!p.publicVisible && (
                <span className="dev-pill" title="Hidden from anonymous visitors; a signed-in developer still sees it">
                  <Lock size={11} aria-hidden="true" /> private
                </span>
              )}
              <span className="dev-pill">{p.usageCount} usage(s)</span>
              <span className="dev-pill">{p.accountCount}/2 accounts</span>
            </div>

            {/* BOTH DATES, labelled, because they answer different questions and
                are the two values in this system easiest to confuse. */}
            <p className="dev-ccard-dates dev-mono-sm">
              <span>
                history from <strong>{p.startDate || "(unset)"}</strong>
              </span>
              <span>
                period{" "}
                <strong>
                  {p.pairStartDate ? `${p.pairStartDate} ${p.pairStartTime || "02:00:00"}` : "(not set)"}
                </strong>
              </span>
            </p>
            <p className="dev-card-text dev-mono-sm" hidden>
              {p.pairStartDate ? `${p.pairStartDate} ${p.pairStartTime || "02:00:00"}` : "no start date"}
            </p>

            {/* THE START DATE, EDITABLE HERE. It is where this container's
                history begins, so moving it moves "overall" and every period
                statistic — the server says so in its response rather than
                leaving it to be noticed. Saved on change: a date input has no
                meaningful half-typed state, so there is nothing to debounce. */}
            <div className="dev-ccard-editdate">
              <label htmlFor={`sd-${p.id}`}>History from</label>
              <input
                id={`sd-${p.id}`}
                className="dev-input dev-input-date"
                type="date"
                value={p.startDate || ""}
                disabled={busy === `s${p.id}`}
                onChange={(e) =>
                  e.target.value && send("PUT", `/${p.id}`, { startDate: e.target.value }, `s${p.id}`)
                }
                title="Where this container's history begins. Overall and the period statistics are measured from it."
              />
            </div>

            {/* THE PAIR, ACCOUNT BY ACCOUNT.
                `accountCount` above says how many are named; this says which of
                them this container is actually RECORDING. The two differ the
                moment an account is released: it stays in the pair, keeps its
                history here, and stops collecting. Without this the page could
                not explain a container whose figures had quietly stopped
                moving for one of its accounts. */}
            {(p.accounts || []).length > 0 && (
              <div className="dev-acct-list">
                {p.accounts.map((a) => (
                  <div key={a.accountId} className={`dev-acct-row${a.live ? " is-live" : ""}`}>
                    {/* TWO LINES, FIXED. Identity and the action on top, state
                        underneath. Everything on one wrapping line put the
                        Release button inline on a short row and on its own line
                        on a long one, so no two cards agreed on row height or on
                        where the button was. */}
                    <div className="dev-acct-top">
                    <span className="dev-acct-id">
                      {a.live ? (
                        <Radio size={12} aria-hidden="true" className="dev-acct-dot" />
                      ) : (
                        <LogOut size={12} aria-hidden="true" className="dev-acct-dot" />
                      )}
                      <b>{a.missing ? "missing account" : a.login}</b>
                      {a.label ? <em>{a.label}</em> : null}
                    </span>

                    {a.live && !a.missing && (
                      <button
                        type="button"
                        className="dev-btn dev-btn-sm"
                        disabled={busy === `r${p.id}${a.login}`}
                        title={
                          `Close ${a.login}'s period in "${p.name}" now. Its trades so far stay here; ` +
                          "another container can then record it."
                        }
                        onClick={() => setReleasing({ containerId: p.id, containerName: p.name, ...a })}
                      >
                        Release
                      </button>
                    )}
                    </div>

                    <div className="dev-acct-meta">
                    <span className="dev-acct-state">
                      {a.live ? (
                        <>recording since {a.livePeriod}</>
                      ) : a.releasedAt ? (
                        <>released {new Date(a.releasedAt * 1000).toISOString().slice(0, 16).replace("T", " ")}</>
                      ) : (
                        <>not recording</>
                      )}
                    </span>

                    {/* SHARED LOGINS. The same login legitimately appears in more
                        than one container - that is how it carries a different
                        name and bot IP per book. What matters is whether another
                        holder is RECORDING it, because then the earlier window
                        claims every trade and this container's figures for it
                        will not move however live it looks. */}
                    {(a.sharedWith || []).length > 0 && (
                      <span
                        className={`dev-acct-shared${
                          a.sharedWith.some((o) => o.live) ? " is-clash" : ""
                        }`}
                      >
                        {a.sharedWith.some((o) => o.live) ? (
                          <>
                            <TriangleAlert size={12} aria-hidden="true" /> also RECORDING in{" "}
                            {a.sharedWith.filter((o) => o.live).map((o) => o.name).join(", ")}
                          </>
                        ) : (
                          <>also in {a.sharedWith.map((o) => o.name).join(", ")}</>
                        )}
                      </span>
                    )}

                    </div>
                  </div>
                ))}
              </div>
            )}

            {p.missing.length > 0 && (
              <p className="dev-ccard-gap">
                <TriangleAlert size={13} aria-hidden="true" /> not live yet — needs{" "}
                {p.missing.join(", ")}
              </p>
            )}

            <div className="dev-ccard-flags">
              {/* Each row is one flag and one sentence about what it does. The
                  three are independent on purpose: a test container you want to watch
                  but keep out of the totals is ENABLED AND EXCLUDED, which is a
                  state a single "test/live" switch cannot express. */}
              <FlagRow
                label="Enabled"
                on={p.enabled}
                busy={busy === `e${p.id}`}
                blocked={p.isDefault && p.enabled ? "The default container cannot be disabled — move the default first." : ""}
                /* Says what disabling COSTS, not just where the book goes.
                   "Out of everything except this page" was true of the pages and
                   silent about the broker: until the poller was wired to this
                   flag, a disabled container kept its sessions open and went on
                   writing deals. Now it does not, and the hint says so, because
                   closing a live session is the part worth knowing before the
                   click. */
                hint={
                  p.enabled
                    ? "In every dropdown, the totals, and polling the broker."
                    : "Out of every page but this one. Its broker sessions are closed."
                }
                onToggle={() => send("PUT", `/${p.id}`, { enabled: !p.enabled }, `e${p.id}`)}
              />
              <FlagRow
                label="In combined totals"
                on={p.includeInOverall}
                busy={busy === `o${p.id}`}
                blocked={p.enabled ? "" : "A disabled container is out of the totals regardless."}
                hint={p.includeInOverall ? "Counted in every combined figure." : "Shown on the All view, counted in none of it."}
                onToggle={() => send("PUT", `/${p.id}`, { includeInOverall: !p.includeInOverall }, `o${p.id}`)}
              />
              <FlagRow
                label="Public"
                on={p.publicVisible}
                busy={busy === `p${p.id}`}
                hint={p.publicVisible ? "Anyone with the report link sees it." : "Developers only — hidden from anonymous visitors."}
                onToggle={() => send("PUT", `/${p.id}`, { publicVisible: !p.publicVisible }, `p${p.id}`)}
              />
              {/* ---------------------------------------------------------
                  HOW THIS BOOK SETTLES. Two switches, independent of each
                  other and of the three above, and "both off" is a real state
                  rather than a mistake: profit simply accumulates as unsettled
                  and only the carry can be banked by hand.

                  Switching withdrawals off is FORWARD ONLY. It cannot be
                  otherwise: the settlement book is replayed from the deals on
                  every rebuild, so a flag the replay read would un-settle every
                  payout the book ever had the moment it was flipped. The hint
                  says so, because "this does not touch what already happened"
                  is the thing a reader needs before clicking, not after.
                  --------------------------------------------------------- */}
              <FlagRow
                label="Settle on withdrawal"
                on={p.settleOnWithdrawal}
                busy={busy === `w${p.id}`}
                hint={
                  p.settleOnWithdrawal
                    ? "A withdrawal banks the profit standing at the time."
                    : "Withdrawals return capital and bank nothing. New payouts only — settlements already recorded are untouched."
                }
                onToggle={() =>
                  send("PUT", `/${p.id}`, { settleOnWithdrawal: !p.settleOnWithdrawal }, `w${p.id}`)
                }
              />
              <FlagRow
                label="Settle period by hand"
                on={p.manualSettlementPeriod}
                busy={busy === `h${p.id}`}
                /* Named for the period, and the hint says what it leaves alone.
                   Settling the CARRY by hand is always available — it banks
                   profit from periods that have closed, which no account panel
                   is showing — so a reader must not read this as turning off
                   manual settlement altogether. */
                hint={
                  p.manualSettlementPeriod
                    ? 'Offers "Settle period…" on the report. Carry is always settleable.'
                    : 'Only "Settle carried…" is offered on the report.'
                }
                onToggle={() =>
                  send(
                    "PUT",
                    `/${p.id}`,
                    { manualSettlementPeriod: !p.manualSettlementPeriod },
                    `h${p.id}`
                  )
                }
              />
              {!p.settleOnWithdrawal && !p.manualSettlementPeriod && (
                <p className="dev-ccard-gap">
                  <TriangleAlert size={13} aria-hidden="true" /> nothing settles automatically in this
                  book — profit accumulates as unsettled, and only the carry can be banked by hand
                </p>
              )}
              <FlagRow
                label="Mode"
                on={p.mode === "live"}
                busy={busy === `m${p.id}`}
                onLabel="live"
                offLabel="test"
                hint="A label only. What it counts towards is the two flags above."
                onToggle={() => send("PUT", `/${p.id}`, { mode: p.mode === "live" ? "test" : "live" }, `m${p.id}`)}
              />
            </div>

            <div className="dev-ccard-foot">
              <button
                type="button"
                className="dev-btn dev-btn-sm dev-btn-quiet"
                disabled={p.isDefault || !p.enabled || busy === `d${p.id}`}
                title={
                  p.isDefault
                    ? "Already the default."
                    : !p.enabled
                    ? "Enable it first — a disabled container cannot be the default."
                    : "Pre-select this container everywhere, and point /investment-portfolio at it."
                }
                onClick={() => send("PUT", `/${p.id}`, { isDefault: true }, `d${p.id}`)}
              >
                <Star size={12} aria-hidden="true" /> Make default
              </button>
              {/* DELETE. Refused for the default container by the server, and
                  disabled here for the same reason so the button does not offer
                  something that cannot happen. */}
              <button
                type="button"
                className="dev-btn dev-btn-sm dev-btn-danger"
                disabled={p.isDefault || busy === `x${p.id}`}
                title={
                  p.isDefault
                    ? "The default container cannot be deleted — make another the default first."
                    : "Delete this container. Its trades and history stay in the database."
                }
                onClick={() => {
                  setDoomed(p);
                  setTyped("");
                }}
              >
                <Trash2 size={12} aria-hidden="true" /> Delete
              </button>
              {p.updatedBy && <span className="dev-ccard-by">last changed by {p.updatedBy}</span>}
            </div>
          </article>
        ))}
      </div>

      {/* THE CRITICAL WARNING.
          A modal rather than a confirm(), because what has to be communicated is
          not "are you sure" but WHAT SURVIVES AND WHAT DOES NOT — and that takes
          more than one line. The counts are the container's real ones, so the
          reader is told exactly how much history is about to become unreachable
          rather than a generic caution.
          The name must be TYPED. The action is one click, its effect is
          invisible on every page afterwards, and the rows it strands are found
          only by running a script; typing the name is the difference between
          deciding and slipping. */}
      {/* RELEASE CONFIRMATION.
          Deliberately lighter than the delete dialog - no typed name - because
          nothing is destroyed: the window closes, the trades stay, and the
          account can come back. What it DOES need to say is the one thing that
          is not obvious, which is that coming back needs a period date this
          account has never used, so the same day will not work. */}
      {releasing && (
        <div className="dev-modal-back" role="dialog" aria-modal="true">
          <div className="dev-modal dev-modal-narrow">
            <div className="dev-modal-head">
              <h3>
                <LogOut size={16} aria-hidden="true" /> Release {releasing.login} from “
                {releasing.containerName}”?
              </h3>
            </div>
            <div className="dev-modal-body">
              <p className="dev-help">
                Its period here closes <strong>now</strong>. Everything it has already traded stays
                in this container and keeps counting towards its figures — nothing is deleted and
                nothing moves.
              </p>
              <ul className="dev-list">
                <li>
                  <strong>{releasing.containerName}</strong> stops recording {releasing.login} and
                  carries on with its other account.
                </li>
                <li>Another container can then record it, from a period starting after now.</li>
                <li>
                  Its unsettled profit stays standing <strong>here</strong>, and is banked by this
                  container's next withdrawal.
                </li>
                <li>
                  To take it back, apply this container's pair again with a period date this account
                  has <strong>never started one on</strong>. A period is identified by login and date
                  only — the time is not part of it — so <strong>the same day cannot be reused</strong>.
                </li>
              </ul>
            </div>
            <div className="dev-modal-foot">
              <div className="dev-modal-actions">
                <button type="button" className="dev-ghost-btn" onClick={() => setReleasing(null)}>
                  Cancel
                </button>
                <button
                  type="button"
                  className="dev-btn dev-btn-primary"
                  disabled={busy === `r${releasing.containerId}${releasing.login}`}
                  onClick={doRelease}
                >
                  {busy === `r${releasing.containerId}${releasing.login}`
                    ? "Releasing…"
                    : "Release this account"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {doomed && (
        <div className="dev-modal-back" role="alertdialog" aria-modal="true">
          <div className="dev-modal dev-modal-narrow">
            <div className="dev-modal-head dev-modal-head-danger">
              <h3>
                <TriangleAlert size={16} aria-hidden="true" /> Delete “{doomed.name}”?
              </h3>
            </div>
            <div className="dev-modal-body">
              <p className="dev-danger-lead">
                This removes the container record. <strong>Nothing that was traded is deleted.</strong>
              </p>

              <ul className="dev-list">
                <li>
                  <strong>{doomed.usageCount}</strong> account period(s),{" "}
                  <strong>{doomed.dealCount}</strong> deal(s),{" "}
                  <strong>{doomed.settlementCount}</strong> settlement(s) and{" "}
                  <strong>{doomed.accountCount}</strong> account(s) STAY in the database.
                </li>
                <li>
                  They will point at a container that no longer exists, so{" "}
                  <strong>no page and no export will show them</strong>.{" "}
                  <code>scripts/pair-doctor.js</code> lists them.
                </li>
                <li>
                  The container's record — including its id — is written to a backup file first.
                  Re-creating it with that id re-attaches all of the above.
                </li>
              </ul>

              <p className="dev-help">
                Type <strong>{doomed.name}</strong> to confirm.
              </p>
              <input
                className="dev-input"
                value={typed}
                autoFocus
                onChange={(e) => setTyped(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Escape") setDoomed(null);
                }}
                aria-label={`Type ${doomed.name} to confirm deletion`}
                placeholder={doomed.name}
              />
            </div>
            <div className="dev-modal-foot">
              <div className="dev-modal-actions">
                <button
                  type="button"
                  className="dev-ghost-btn"
                  onClick={() => {
                    setDoomed(null);
                    setTyped("");
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="dev-btn dev-btn-danger-strong"
                  /* Compared case-insensitively and trimmed, matching the
                     server's own check — so the button and the route agree about
                     what counts as confirmed. */
                  disabled={
                    typed.trim().toLowerCase() !== doomed.name.trim().toLowerCase() ||
                    busy === `x${doomed.id}`
                  }
                  onClick={() => destroy(doomed)}
                >
                  {busy === `x${doomed.id}` ? "Deleting…" : "Delete this container"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

/** One flag, with what it does written next to it rather than in a tooltip. */
function FlagRow({ label, on, busy, hint, blocked, onToggle, onLabel = "on", offLabel = "off" }) {
  return (
    <div className="dev-flagrow">
      <button
        type="button"
        className={`dev-switch${on ? " is-on" : ""}`}
        onClick={onToggle}
        disabled={busy || Boolean(blocked)}
        title={blocked || undefined}
        aria-pressed={on}
        aria-label={`${label}: ${on ? onLabel : offLabel}`}
      >
        <span className="dev-switch-knob" aria-hidden="true" />
      </button>
      <span className="dev-flagrow-l">{label}</span>
      <span className="dev-flagrow-v">{on ? onLabel : offLabel}</span>
      <span className="dev-flagrow-h">{blocked || hint}</span>
    </div>
  );
}

